<script lang="ts" setup>
import AssociationSelect from "@/components/common/AssociationSelect.vue";
import { useAssociationOptions } from "@/composition/useAssociationOptions";
import config from "@/config";
import { useAppStore } from "@/store/app";
import { buildPairAssociationSettings } from "@/util/associationUtils";
import { PRODUCT_TYPE } from "@/util/constants";
import { collectFieldModels, getModelDisplayName } from "@/util/customObjectUtils";
import currency from "@/util/currency";
import { CursorBoxIcon, PlusIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIDivider,
  UIInputNumber,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIModalHeader,
  UISelect,
} from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const store = useAppStore();
const { getPipelinesFromStore } = storeToRefs(store);

const emit = defineEmits(["update:cancel", "update:success"]);

const OPPORTUNITY_MODEL = "opportunity";

/** The non-opportunity object of the current pair (company/custom), if any. Guarded
 * against stale store state: both the opportunity and the paired object must have a
 * field present on the form/slides, otherwise there is no pair to associate. */
const pairedObjectModel = computed<string | undefined>(() => {
  const candidate = [store.currentCustomObjectModel, store.currentWithObjectModel].find(
    (model) => model && model !== OPPORTUNITY_MODEL
  );
  if (!candidate) {
    return undefined;
  }
  const models = collectFieldModels(
    store.product === PRODUCT_TYPE.FORM ? store.elements : null,
    store.product !== PRODUCT_TYPE.FORM ? store.slides : null
  );
  return models.includes(OPPORTUNITY_MODEL) && models.includes(candidate)
    ? candidate
    : undefined;
});

const associationObjectModel = ref(OPPORTUNITY_MODEL);
const {
  options: associationOptions,
  loading: associationLoading,
  error: associationError,
  isObjectPairMode,
  fetchAssociations,
  findOption,
} = useAssociationOptions(associationObjectModel, pairedObjectModel);

const selectedAssociationId = ref<string>("");

/**
 * In pair mode a valid association is required before Save. Block it while the
 * options are still loading/errored or none are selected/available, so the user
 * cannot persist a pair with a missing or unresolved association (which the
 * backend links inconsistently). Non-pair (contact/opportunity-only) edits are
 * never blocked here.
 */
const isSaveDisabled = computed(() => {
  if (!isObjectPairMode.value) {
    return false;
  }
  return (
    associationLoading.value ||
    !!associationError.value ||
    !selectedAssociationId.value ||
    associationOptions.value.length === 0
  );
});

const pairedObjectDisplay = computed(() =>
  pairedObjectModel.value
    ? getModelDisplayName(pairedObjectModel.value, store.customObjects || [], t)
    : ""
);

const newAssociationLabel = computed(() =>
  t("common.newAssociationBetween", {
    objectA: pairedObjectDisplay.value,
    objectB: t("common.opportunity"),
  })
);

/** Open the association settings page — prefer whichever paired side is a real
 * custom object, else the general objects settings page. */
const createNewAssociation = () => {
  const customObject = store.customObjects?.find(
    (object: any) => object.key === pairedObjectModel.value
  );
  const base = `${config.mode === "dev" ? "http" : "https"}://${
    store.company.domain ? store.company.domain : config.GHL_DOMAIN
  }/v2/location/${store.locationId}/settings/objects`;
  const url = customObject?.id ? `${base}/${customObject.id}/associations` : base;
  window.open(url, "_blank");
};

// Load associations + restore the saved selection whenever the modal opens in pair mode.
watch(
  () => props.show,
  (show) => {
    if (show && isObjectPairMode.value) {
      const saved = store.contactAssociationSettings;
      selectedAssociationId.value = Array.isArray(saved?.selectedAssociation)
        ? saved?.selectedAssociation?.[0] || ""
        : "";
      fetchAssociations();
    }
  },
  { immediate: true }
);

const nameMap = ({ name }) => ({
  label: name,
  value: name,
});

const cancelCallback = () => {
  opportunitySettings.value = cloneDeep(store.opportunitySettings) || {
    selectedPipeline: pipelineNames.value?.[0]?.value,
    stage: stageOptions.value?.[0]?.value,
    status: statusOptions.value?.[0]?.value,
    opportunityValue: 0,
  };
  emit("update:cancel", false);
};

const submitCallback = () => {
  const {
    selectedPipeline = "",
    stage = "",
    status = "",
    opportunityValue = "",
  } = opportunitySettings.value;

  const {
    id: pipelineId,
    name,
    stages,
  } = (getPipelinesFromStore.value ?? []).find(({ name }) => name === selectedPipeline) ?? {};

  const { id: pipelineStageId } = (stages ?? []).find(({ name }) => name === stage) ?? {};

  let opportunityCustomFields = store.formId
    ? store.elements.filter((field) => field.model === "opportunity")
    : store.slides.reduce((acc, slide) => {
        const filteredFields = slide.slideData.filter((field) => field.model === "opportunity");
        return acc.concat(filteredFields);
      }, []);

  opportunityCustomFields = opportunityCustomFields.map(({ Id, fieldKey }) => ({
    id: Id,
    key: fieldKey.replace("opportunity.", ""),
  }));

  // In pair mode, also carry the chosen object↔object association so the parent
  // persists both the placement and the association from a single Save. The helper
  // keeps withObjectModel and the two object keys atomic — if the selection hasn't
  // resolved to an option with both keys (e.g. Save before fetch resolves), it emits
  // no association rather than a half-populated config the backend mishandles.
  const selected = findOption(selectedAssociationId.value);
  const contactAssociation = isObjectPairMode.value
    ? buildPairAssociationSettings(
        OPPORTUNITY_MODEL,
        pairedObjectModel.value,
        selectedAssociationId.value,
        selected
      )
    : undefined;

  emit("update:success", {
    ...opportunitySettings.value,
    payload: {
      pipelineId,
      locationId: store.locationId,
      name,
      pipelineStageId,
      status: status,
      monetaryValue: parseFloat(opportunityValue),
      customFields: opportunityCustomFields,
    },
    contactAssociation,
  });
};

const pipelineNames = ref(getPipelinesFromStore.value.map(nameMap));

const stageOptions = ref(getPipelinesFromStore.value[0]?.stages.map(nameMap));

const statusOptions = ref([
  { label: t("common.open"), value: "open" },
  { label: t("common.won"), value: "won" },
  { label: t("common.lost"), value: "lost" },
  { label: t("common.abandon"), value: "abandon" },
]);

const opportunitySettings = ref(
  cloneDeep(store.opportunitySettings) || {
    selectedPipeline: pipelineNames.value?.[0]?.value,
    stage: stageOptions.value?.[0]?.value,
    status: statusOptions.value?.[0]?.value,
    opportunityValue: 0,
  }
);

if (
  !getPipelinesFromStore.value?.find(
    ({ name }) => name === opportunitySettings.value?.selectedPipeline
  )
) {
  opportunitySettings.value = {
    selectedPipeline: pipelineNames.value?.[0]?.value,
    stage: stageOptions.value?.[0]?.value,
    status: statusOptions.value?.[0]?.value,
    opportunityValue: 0,
  };
}
const updateSelectedPipeline = (value: string) => {
  stageOptions.value = getPipelinesFromStore.value
    .find(({ name }) => name === value)
    ?.stages.map(({ name }) => ({
      label: name,
      value: name,
    }));
  opportunitySettings.value.stage = stageOptions.value?.[0].value;
};

const openOpportunitySettings = () =>
  window
    .open(
      `${config.mode === "dev" ? "http" : "https"}://${
        store.company.domain ? store.company.domain : config.GHL_DOMAIN
      }/v2/location/${store.locationId}/opportunities/pipeline`,
      "_blank"
    )
    .focus();

const currentLocationCurrency = currency?.[store.locationCountry as any] || "$";

const modalSubtitle = computed(() =>
  isObjectPairMode.value && pairedObjectDisplay.value
    ? t("common.opportunitySetupSubtitleLinked", { object: pairedObjectDisplay.value })
    : t("common.opportunitySetupSubtitle")
);
</script>

<template>
  <UIModal :show="show" @update:show="cancelCallback">
    <template #header>
      <UIModalHeader
        id="modal-header"
        :title="t('common.opportunitySetupTitle')"
        :description="modalSubtitle"
        :icon="CursorBoxIcon"
        @close="cancelCallback"
      >
      </UIModalHeader>
    </template>
    <UIModalContent>
      <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {{ t("common.opportunityPlacement") }}
      </div>

      <div class="mb-6 flex gap-x-4">
        <div class="w-full">
          <div class="mb-1 text-sm font-medium">{{ t("common.selectAPipeline") }}</div>
          <UISelect
            id="select-a-pipeline"
            v-model:value="opportunitySettings.selectedPipeline"
            :options="pipelineNames"
            :placeholder="t('common.select')"
            @update:value="updateSelectedPipeline"
          >
            <template #action>
              <div class="w-full border-t border-gray-100 pt-1">
                <UIButton
                  id="create-new-pipeline"
                  text
                  type="primary"
                  :disabled="!store.isOpportunitiesEnabled"
                  @click="openOpportunitySettings"
                >
                  <PlusIcon class="mr-2 h-4 w-4" /> {{ t("common.newPipeline") }}
                </UIButton>
              </div>
            </template>
          </UISelect>
        </div>
        <div class="w-full">
          <div class="mb-1 text-sm font-medium">{{ t("common.stage") }}</div>
          <UISelect
            id="stage"
            v-model:value="opportunitySettings.stage"
            :options="stageOptions"
            :placeholder="t('common.select')"
          />
        </div>
      </div>

      <div class="mb-8 flex gap-x-4">
        <div class="w-full">
          <div class="mb-1 text-sm font-medium">{{ t("common.status") }}</div>
          <UISelect
            id="status"
            v-model:value="opportunitySettings.status"
            :options="statusOptions"
            :placeholder="t('common.select')"
          />
        </div>
        <div class="w-full">
          <div class="mb-1 text-sm font-medium">{{ t("common.opportunityValue") }}</div>
          <UIInputNumber id="opportunity-value" v-model:value="opportunitySettings.opportunityValue"
            ><template #prefix>{{ currentLocationCurrency }}</template></UIInputNumber
          >
        </div>
      </div>

      <!-- Association section: only when the opportunity is paired with another object -->
      <template v-if="isObjectPairMode">
        <UIDivider class="mb-4" />
        <div class="mb-1 flex items-center gap-2">
          <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {{ t("common.association") }}
          </span>
          <span class="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {{ pairedObjectDisplay }} → {{ t("common.opportunity") }}
          </span>
        </div>
        <div class="mb-2 text-sm text-gray-500">
          {{ t("common.pickHowRecordsLink") }}
        </div>
        <AssociationSelect
          v-model="selectedAssociationId"
          :options="associationOptions"
          :loading="associationLoading"
          :error="associationError"
          :create-label="newAssociationLabel"
          allow-create
          @create="createNewAssociation"
        />
      </template>
    </UIModalContent>
    <template #footer>
      <UIModalFooter
        id="modal-footer"
        :positive-text="t('common.save')"
        :negative-text="t('common.cancel')"
        :disabled="isSaveDisabled"
        @positive-click="submitCallback"
        @negative-click="cancelCallback"
      />
    </template>
  </UIModal>
</template>
